import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Lock, User, QrCode } from "lucide-react";

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const token = searchParams.get("token");
  const [invite, setInvite] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) {
        setError("This invitation link is invalid or incomplete.");
        setChecking(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/invitations/${token}`);
        setInvite(res.data);
        setForm((f) => ({ ...f, name: res.data.name || "" }));
      } catch (err) {
        setError(err.response?.data?.detail || "This invitation is invalid or has expired.");
      } finally {
        setChecking(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Please enter your full name");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/invitations/accept`, {
        token,
        password: form.password,
        name: form.name,
      });
      setSession(res.data.access_token, res.data.user);
      toast.success("Welcome aboard!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card className="shadow-xl border-0 bg-card/80 backdrop-blur">
          <CardHeader className="text-center">
            <div
              className="mx-auto h-12 w-12 rounded-xl flex items-center justify-center mb-4 text-white"
              style={{ backgroundColor: invite?.primary_color || "#4F46E5" }}
            >
              {invite?.company_logo ? (
                <img
                  src={invite.company_logo.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${invite.company_logo}` : invite.company_logo}
                  alt={invite.company_name}
                  className="h-8 w-auto"
                />
              ) : (
                <QrCode className="h-6 w-6" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {invite ? `Join ${invite.company_name}` : "Accept Invitation"}
            </CardTitle>
            <CardDescription>
              {invite ? `Set up your digital business card for ${invite.email}` : "Create your account"}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {checking ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="text-center py-6" data-testid="invite-error">
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button asChild variant="outline" className="w-full h-12 rounded-full mt-6">
                  <Link to="/">Go Home</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      data-testid="invite-name-input"
                      placeholder="John Doe"
                      className="pl-10 h-12"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Create Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      data-testid="invite-password-input"
                      type="password"
                      placeholder="At least 6 characters"
                      className="pl-10 h-12"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full h-12 rounded-full" disabled={loading} data-testid="invite-submit-btn">
                  {loading ? "Creating account..." : "Create Account & Join"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AcceptInvitePage;
